import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError, API_BASE } from "@/lib/api";
import { sumColl } from "./AdminDailyDetail";
import { usePump } from "@/context/PumpContext";
import { useProducts, emptyTesting, DEFAULT_PRODUCTS } from "@/lib/products";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Download, FileSpreadsheet, Filter, Pencil, Trash2, Save, Printer } from "lucide-react";
import { fmtINR, fmtNum, fmtDate, fmtTime } from "@/utils/format";
import { toast } from "sonner";

const isoDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

export default function AdminShifts() {
  const nav = useNavigate();
  const { pump } = usePump();
  const products = useProducts();
  const MPDS = Array.from({ length: pump?.mpd_count || 2 }, (_, i) => `MPD ${i + 1}`);
  const SHIFTS = Array.from({ length: pump?.shift_count || 3 }, (_, i) => i + 1);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState({ business_date: "", from_date: isoDaysAgo(29), to_date: "", shift: "", employee_id: "", mpd: "" });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // shift being edited
  const [confirmDel, setConfirmDel] = useState(null); // shift being deleted
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/employees").then((r) => setEmployees(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const load = async () => {
    setLoading(true);
    const params = {};
    Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/shifts", { params });
      setShifts(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  const download = (kind) => {
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => { if (v) params.append(k, v); });
    const token = localStorage.getItem("bep_token");
    const url = `${API_BASE}/export/shifts.${kind}?${params.toString()}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error("Export failed");
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `shifts_${filter.business_date || "all"}.${kind}`;
        a.click();
      })
      .catch((e) => toast.error(e.message));
  };

  const openEdit = (s) => {
    setEditing({
      id: s.id,
      mpd: s.mpd,
      shift_number: String(s.shift_number),
      business_date: s.business_date,
      opening: { ...s.opening },
      closing: s.closing ? { ...s.closing } : null,
      products: s.products_used || products,
      testing: s.testing ? { ...s.testing } : emptyTesting(s.products_used || products),
      collection: s.collection ? { paytm: s.collection.paytm, credit: s.collection.credit, card: s.collection.card } : { paytm: 0, credit: 0, card: 0 },
      isDual: !!s.opening_mpd2,
      opening_mpd2: s.opening_mpd2 ? { ...s.opening_mpd2 } : null,
      closing_mpd2: s.closing_mpd2 ? { ...s.closing_mpd2 } : null,
      testing_mpd2: s.testing_mpd2 ? { ...s.testing_mpd2 } : emptyTesting(s.products_used || products),
      collection_mpd2: s.collection_mpd2 ? { paytm: s.collection_mpd2.paytm, credit: s.collection_mpd2.credit, card: s.collection_mpd2.card } : { paytm: 0, credit: 0, card: 0 },
      status: s.status,
    });
  };

  const numObj = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, Number(v)]));
  const numTest = (t) => ({ ...Object.fromEntries(editing.products.map((p) => [p.key, Number(t?.[p.key] || 0)])), remarks: t?.remarks || null });
  const numColl = (c) => ({ paytm: Number(c?.paytm || 0), credit: Number(c?.credit || 0), card: Number(c?.card || 0) });

  const saveEdit = async () => {
    setSaving(true);
    try {
      const body = {
        mpd: editing.mpd,
        shift_number: Number(editing.shift_number),
        business_date: editing.business_date,
        opening: numObj(editing.opening),
      };
      if (editing.closing) {
        body.closing = numObj(editing.closing);
        body.testing = numTest(editing.testing);
        body.collection = numColl(editing.collection);
        if (editing.isDual) {
          body.opening_mpd2 = numObj(editing.opening_mpd2);
          if (editing.closing_mpd2) body.closing_mpd2 = numObj(editing.closing_mpd2);
          body.testing_mpd2 = numTest(editing.testing_mpd2);
          body.collection_mpd2 = numColl(editing.collection_mpd2);
        }
      }
      await api.patch(`/shifts/${editing.id}`, body);
      toast.success("Shift updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/shifts/${confirmDel.id}`);
      toast.success("Shift deleted");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <AdminLayout>
      <h1 className="text-3xl font-display font-bold mb-1">All Shifts</h1>
      <p className="text-sm text-slate-500 mb-6">Showing last 30 days by default. Filter, edit, delete, export, and open any DSM slip.</p>

      <Card className="p-4 rounded-2xl border-slate-200 mb-5">
        <div className="grid md:grid-cols-4 lg:grid-cols-8 gap-3 items-end">
          <div>
            <Label className="text-xs">From</Label>
            <Input
              data-testid="filter-from"
              type="date"
              className="h-11 rounded-xl mt-1"
              value={filter.from_date}
              onChange={(e) => setFilter((f) => ({ ...f, from_date: e.target.value }))}
            />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input
              data-testid="filter-to"
              type="date"
              className="h-11 rounded-xl mt-1"
              value={filter.to_date}
              onChange={(e) => setFilter((f) => ({ ...f, to_date: e.target.value }))}
            />
          </div>
          <div>
            <Label className="text-xs">Exact Date</Label>
            <Input
              data-testid="filter-date"
              type="date"
              className="h-11 rounded-xl mt-1"
              value={filter.business_date}
              onChange={(e) => setFilter((f) => ({ ...f, business_date: e.target.value }))}
            />
          </div>
          <div>
            <Label className="text-xs">Shift</Label>
            <Select value={filter.shift || "all"} onValueChange={(v) => setFilter((f) => ({ ...f, shift: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="filter-shift"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {SHIFTS.map((n) => <SelectItem key={n} value={String(n)}>Shift {n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Employee</Label>
            <Select value={filter.employee_id || "all"} onValueChange={(v) => setFilter((f) => ({ ...f, employee_id: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="filter-employee"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_id})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">MPD</Label>
            <Select value={filter.mpd || "all"} onValueChange={(v) => setFilter((f) => ({ ...f, mpd: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="filter-mpd"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {MPDS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                <SelectItem value="Both MPDs">Both MPDs</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={load} data-testid="apply-filter-btn" className="h-11 rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]">
            <Filter className="w-4 h-4 mr-2" /> Apply
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => download("xlsx")} data-testid="export-xlsx-btn" className="h-11 rounded-xl flex-1">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
            </Button>
            <Button variant="outline" onClick={() => download("pdf")} data-testid="export-pdf-btn" className="h-11 rounded-xl flex-1">
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase text-slate-500">
                <th className="py-3 px-3">Date</th>
                <th className="py-3 px-3">Shift</th>
                <th className="py-3 px-3">Employee</th>
                <th className="py-3 px-3">MPD</th>
                <th className="py-3 px-3">Time</th>
                {products.map((p) => <React.Fragment key={p.key}><th className="py-3 px-3 text-right">{p.short} {p.unit}</th><th className="py-3 px-3 text-right">{p.short} ₹</th></React.Fragment>)}
                <th className="py-3 px-3 text-right">Testing</th>
                <th className="py-3 px-3 text-right">Total Sale</th>
                <th className="py-3 px-3 text-right">Paytm</th>
                <th className="py-3 px-3 text-right">Credit</th>
                <th className="py-3 px-3 text-right">Card</th>
                <th className="py-3 px-3 text-right">Cash</th>
                <th className="py-3 px-3">Status</th>
                <th className="py-3 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={13 + products.length * 2} className="py-8 text-center text-slate-500">Loading…</td></tr>}
              {!loading && shifts.length === 0 && <tr><td colSpan={13 + products.length * 2} className="py-8 text-center text-slate-500">No shifts found.</td></tr>}
              {shifts.map((s) => { const c = sumColl(s); const t = s.totals || {}; return (
                <tr key={s.id} data-testid={`shift-row-${s.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-2.5 px-3 whitespace-nowrap">{fmtDate(s.business_date)}</td>
                  <td className="py-2.5 px-3">S{s.shift_number}</td>
                  <td className="py-2.5 px-3 whitespace-nowrap">{s.employee_name} <span className="text-slate-400">({s.employee_code})</span></td>
                  <td className="py-2.5 px-3 whitespace-nowrap">{s.mpd}</td>
                  <td className="py-2.5 px-3 font-mono-num text-xs whitespace-nowrap">{fmtTime(s.start_time)} → {s.end_time ? fmtTime(s.end_time) : "—"}</td>
                  {products.map((p) => <React.Fragment key={p.key}><td className="py-2.5 px-3 font-mono-num text-right">{fmtNum(t[`net_${p.key}`])}</td><td className="py-2.5 px-3 font-mono-num text-right">{fmtINR(t[`sales_${p.key}`])}</td></React.Fragment>)}
                  <td className="py-2.5 px-3 font-mono-num text-right">{fmtNum(t.total_testing_litres)}</td>
                  <td className="py-2.5 px-3 font-mono-num text-right font-semibold text-[color:var(--bpcl-blue)]">{fmtINR(t.grand_sales)}</td>
                  <td className="py-2.5 px-3 font-mono-num text-right">{fmtINR(c.paytm)}</td>
                  <td className="py-2.5 px-3 font-mono-num text-right">{fmtINR(c.credit)}</td>
                  <td className="py-2.5 px-3 font-mono-num text-right">{fmtINR(c.card)}</td>
                  <td className="py-2.5 px-3 font-mono-num text-right font-semibold text-emerald-700">{fmtINR(c.cash)}</td>
                  <td className="py-2.5 px-3">
                    {s.status === "running" ? (
                      <Badge className="rounded-full bg-[color:var(--bpcl-orange)]/15 text-[color:var(--bpcl-orange-dark)] border-transparent">Running</Badge>
                    ) : (
                      <Badge variant="secondary" className="rounded-full">Done</Badge>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right whitespace-nowrap space-x-1">
                    {s.status === "completed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full h-8"
                        onClick={() => nav(`/shift/${s.id}/print`)}
                        data-testid={`slip-shift-${s.id}`}
                      >
                        <Printer className="w-3.5 h-3.5 mr-1" /> Slip
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full h-8"
                      onClick={() => openEdit(s)}
                      data-testid={`edit-shift-${s.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setConfirmDel(s)}
                      data-testid={`delete-shift-${s.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit shift</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Business date</Label>
                  <Input
                    data-testid="edit-business-date"
                    type="date"
                    value={editing.business_date}
                    onChange={(e) => setEditing({ ...editing, business_date: e.target.value })}
                    className="h-11 rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Shift</Label>
                  <Select value={editing.shift_number} onValueChange={(v) => setEditing({ ...editing, shift_number: v })}>
                    <SelectTrigger data-testid="edit-shift-number" className="h-11 rounded-xl mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SHIFTS.map((n) => <SelectItem key={n} value={String(n)}>Shift {n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">MPD</Label>
                  <Select value={editing.mpd} onValueChange={(v) => setEditing({ ...editing, mpd: v })}>
                    <SelectTrigger data-testid="edit-mpd" className="h-11 rounded-xl mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MPDS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      <SelectItem value="Both MPDs">Both MPDs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <ReadingBlock products={editing.products}
                title={editing.isDual ? "MPD 1 · Opening readings" : "Opening readings"}
                values={editing.opening}
                onChange={(k, v) => setEditing({ ...editing, opening: { ...editing.opening, [k]: v } })}
                prefix="edit-open"
              />
              {editing.closing && (
                <>
                  <ReadingBlock products={editing.products}
                    title={editing.isDual ? "MPD 1 · Closing readings" : "Closing readings"}
                    values={editing.closing}
                    onChange={(k, v) => setEditing({ ...editing, closing: { ...editing.closing, [k]: v } })}
                    prefix="edit-close"
                  />
                  <TripleBlock label="Testing" keys={editing.products.map((p) => p.key)} values={editing.testing} prefix="edit-test"
                    onChange={(k, v) => setEditing({ ...editing, testing: { ...editing.testing, [k]: v } })} />
                  <TripleBlock label="Collection (₹) — Paytm / Credit / Card" keys={["paytm", "credit", "card"]} values={editing.collection} prefix="edit-coll"
                    onChange={(k, v) => setEditing({ ...editing, collection: { ...editing.collection, [k]: v } })} />
                  {editing.isDual && (
                    <>
                      <ReadingBlock products={editing.products} title="MPD 2 · Opening readings" values={editing.opening_mpd2} prefix="edit-open2"
                        onChange={(k, v) => setEditing({ ...editing, opening_mpd2: { ...editing.opening_mpd2, [k]: v } })} />
                      {editing.closing_mpd2 && (
                        <ReadingBlock products={editing.products} title="MPD 2 · Closing readings" values={editing.closing_mpd2} prefix="edit-close2"
                          onChange={(k, v) => setEditing({ ...editing, closing_mpd2: { ...editing.closing_mpd2, [k]: v } })} />
                      )}
                      <TripleBlock label="MPD 2 · Testing" keys={editing.products.map((p) => p.key)} values={editing.testing_mpd2} prefix="edit-test2"
                        onChange={(k, v) => setEditing({ ...editing, testing_mpd2: { ...editing.testing_mpd2, [k]: v } })} />
                      <TripleBlock label="MPD 2 · Collection (₹) — Paytm / Credit / Card" keys={["paytm", "credit", "card"]} values={editing.collection_mpd2} prefix="edit-coll2"
                        onChange={(k, v) => setEditing({ ...editing, collection_mpd2: { ...editing.collection_mpd2, [k]: v } })} />
                    </>
                  )}
                  <div className="text-xs text-slate-500">Saving recomputes litres, fuel amounts, grand total, expected cash and any recorded shortage / excess — daily report and DSM slip update automatically.</div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-xl">Cancel</Button>
            <Button
              onClick={saveEdit}
              disabled={saving}
              data-testid="save-shift-btn"
              className="rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]"
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this shift?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDel && (
                <>Removing shift <b>{fmtDate(confirmDel.business_date)}</b> · Shift {confirmDel.shift_number} · {confirmDel.employee_name}. This action cannot be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="confirm-delete-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

function TripleBlock({ label, keys, values, onChange, prefix }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="grid grid-cols-3 gap-2 mt-1">
        {keys.map((k) => (
          <div key={k}>
            <div className="text-[10px] uppercase text-slate-500 mb-0.5">{k}</div>
            <Input data-testid={`${prefix}-${k}`} type="number" step="0.01" min="0" inputMode="decimal"
              value={values?.[k] ?? 0} onChange={(e) => onChange(k, e.target.value)} className="h-11 rounded-xl font-mono-num" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ReadingBlock({ title, values, onChange, prefix, products }) {
  const fields = products.flatMap((p) => p.nozzles.map((nz, i) => ({ k: nz, l: `${p.short} N${i + 1}` })));
  return (
    <div>
      <Label className="text-xs">{title}</Label>
      <div className="grid grid-cols-3 gap-2 mt-1">
        {fields.map(({ k, l }) => (
          <div key={k}>
            <div className="text-[10px] uppercase text-slate-500 mb-0.5">{l}</div>
            <Input
              data-testid={`${prefix}-${k}`}
              type="number"
              step="0.01"
              inputMode="decimal"
              value={values?.[k] ?? ""}
              onChange={(e) => onChange(k, e.target.value)}
              className="h-11 rounded-xl font-mono-num"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
